import React, { useState, useContext, useMemo } from 'react';
import Form, { useForm } from 'components/molecules/Form';
import Toast from 'components/molecules/Toast';
import { AuthContext } from 'context/authContext';
import { SyledEstimation } from './Estimation.styles';
import pakFlagImg from '../../assets/images/pak-flag-icon.svg';
import chinaFlagImg from '../../assets/images/china-flag-icon.svg';
import estimationService from 'services/estimationService';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Import sub-components
import EstimateHeader from './components/EstimateHeader';
import ClientDetails from './components/ClientDetails';
import EstimateItem from './components/EstimateItem';
import VendorComparison from './components/VendorComparison';
import VendorNotes from './components/VendorNotes';
import Button from 'components/atoms/Button';
import productEnquiryService from 'services/productEnquiryService';

// ── Option Lists ──
const unitOpts = [
  { label: 'inch', value: 'inch' },
  { label: 'cm', value: 'cm' },
  { label: 'mm', value: 'mm' },
];

const packagingTypeOpts = [
  { label: 'Rigid Box', value: 'rigid_box' },
  { label: 'Folding Carton', value: 'folding_carton' },
  { label: 'Corrugated Box', value: 'corrugated_box' },
  { label: 'Mailer Box', value: 'mailer_box' },
];

const printOptions = [
  { label: '4/0 Color (CMYK) Outside', value: 'cmyk_outside' },
  { label: 'Full Color Ouside', value: 'full_color_outside' },
  { label: 'Full Color Both Sides', value: 'full_color_both_sides' },
  { label: 'Full Color Inside', value: 'full_color_inside' },
  { label: 'CMYK + Pantone', value: 'cmyk_pantone' },
  { label: 'Pantone Only', value: 'pantone_only' },
  { label: 'Digital Print', value: 'digital_print' },
];

const finishOptions = [
  { label: 'Embossing', value: 'embossing' },
  { label: 'Debossing', value: 'debossing' },
  { label: 'Foiling', value: 'foiling' },
  { label: 'Spot UV', value: 'spot_uv' },
  { label: 'Window Patching', value: 'window_patching' },
];

const stockOptions = [
  { label: '270gsm + E Flute', value: '270gsm_e_flute' },
  { label: '350gsm + E Flute + 250gsm (Both Sides Print)', value: '350gsm_e_flute_250gsm_both_sides' },
  { label: '300gsm + B Flute', value: '300gsm_b_flute' },
  { label: '300gsm + E Flute', value: '300gsm_e_flute' },
  { label: '350gsm + E Flute', value: '350gsm_e_flute' },
  { label: 'White Kraft + E Flute', value: 'white_kraft_e_flute' },
  { label: 'Brown Kraft + E Flute', value: 'brown_kraft_e_flute' },
  { label: 'Brown Kraft + B Flute', value: 'brown_kraft_b_flute' },
  { label: 'SBS 18pt', value: 'sbs_18pt' },
  { label: 'SBS 24pt', value: 'sbs_24pt' },
  { label: '1200gsm Greyboard', value: '1200gsm_greyboard' },
  { label: '1400gsm Greyboard', value: '1400gsm_greyboard' },
  { label: 'Other / Custom', value: 'custom' },
];

const laminationOptions = [
  { label: 'Matte', value: 'matte' },
  { label: 'Gloss', value: 'gloss' },
  { label: 'Soft Touch', value: 'soft-touch' },
  { label: 'No Lamination', value: 'no-lamination' },
];

const currencyOpts = [
  { label: 'USD', value: 'usd' },
  { label: 'PKR', value: 'pkr' },
];

const SHIPPING_METHODS = {
  pk: [
    { label: 'AIR RUSH (5-6 days)', value: 'air_rush' },
    { label: 'AIR CARGO (10-12 days)', value: 'air_cargo' },
  ],
  cn: [
    { label: 'AIR RUSH (5-6 days)', value: 'air_rush' },
    { label: 'AIR CARGO (10-12 days)', value: 'air_cargo' },
    { label: 'SEA (22-28 days)', value: 'sea' },
  ],
  calc: [{ label: 'AIR RUSH (5-6 days)', value: 'AIR RUSH (5-6 days)' }],
};

// Vendors config
const VENDORS = [
  { key: 'pk', label: 'Pakistan Vendor', short: 'PK', flagImg: pakFlagImg },
  { key: 'cn', label: 'China Vendor', short: 'CN', flagImg: chinaFlagImg },
  { key: 'calc', label: 'In House', short: 'In-house', flagImg: null },
];

// ── Helper: format currency ──
const fmt = v => {
  const n = Number(v) || 0;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const CreateEstimate = ({ onClose }) => {
  const [form] = useForm();
  const navigate = useNavigate();
  const { refetch, user } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState({
    page: 1,
    pageSize: 10,
    searchText: '',
    startDate: '',
    endDate: '',
  });

  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isView = searchParams.get('view') === 'true';

  const { estimations_data } = estimationService.GetEstimations(
    editId ? { ...searchQuery, getAll: true } : searchQuery,
    refetch,
  );

  const { product_enquires_data, product_enquires_loading } = productEnquiryService.GetProductEnquiries(
    editId ? { ...searchQuery, getAll: true } : searchQuery,
    refetch,
  );

  const totalToday = estimations_data?.totalItems || 0;

  const [nextItemId, setNextItemId] = useState(2);
  const [loading, setLoading] = useState(false);
  const [shippingAddresses, setShippingAddresses] = useState([{ id: Date.now() }]);
  const addAddress = () => setShippingAddresses(prev => [...prev, { id: Date.now() }]);
  const removeAddress = id => {
    if (shippingAddresses.length > 1) {
      setShippingAddresses(prev => prev.filter(a => a.id !== id));
    }
  };

  // Selected tier per item: { itemId: tierId }
  const [itemSelectedTiers, setItemSelectedTiers] = useState({});

  // Estimate Items
  const [estimateItems, setEstimateItems] = useState([
    {
      id: 1,
      name: 'Burger Box',
      packagingType: 'folding_carton',
      printing: 'cmyk_outside',
      finishes: 'spot_uv',
      lamination: 'matte',
      stock: '350gsm_e_flute',
      dimLength: 120,
      dimWidth: 120,
      dimHeight: 80,
      dimUnit: 'mm',
      unitWeight: 0.1,
      margin: 50,
      selectedVendors: ['pk'],
      tiers: [{ id: 101, quantity: 500 }],
    },
  ]);

  // Cost Data: { itemId: { vendorKey: { tierId: cost } } }
  const [vendorCosts, setVendorCosts] = useState({});

  // Shipping Data: { itemId: { vendorKey: { weight, ratePerKg, method } } }
  const [shippingData, setShippingData] = useState({});

  React.useEffect(() => {
    if (editId && estimations_data?.estimations?.length > 0) {
      const estimation = estimations_data.estimations.find(e => e._id === editId);
      if (estimation) {
        const formValues = {
          clientName: estimation.client?.name,
          companyName: estimation.client?.company,
          email: estimation.client?.email,
          phone: estimation.client?.phone,
          project_details: estimation.client?.details,
          notes: estimation.vendorNotes,
        };

        const newAddresses = estimation.client?.shippingAddresses?.map((addr, idx) => {
          formValues[`shippingAddress_${idx}`] = addr.address;
          formValues[`shippingDetails_${idx}`] = addr.details;
          return { id: Date.now() + idx };
        }) || [{ id: Date.now() }];

        setShippingAddresses(newAddresses);
        form.setFieldsValue(formValues);

        const newVendorCosts = {};
        const newShippingData = {};

        const newItems = estimation.items.map(item => {
          const uniqueTiers = [];
          const tierMap = new Map();

          item.comparisons.forEach(comp => {
            if (!tierMap.has(comp.tierId)) {
              const uiTierId = Date.now() + Math.random() + comp.tierId;
              tierMap.set(comp.tierId, uiTierId);
              uniqueTiers.push({
                id: uiTierId,
                originalTierId: comp.tierId,
                quantity: comp.tierQuantity,
              });
            }
          });

          const itemId = item.id;
          newVendorCosts[itemId] = {};
          newShippingData[itemId] = {};

          item.comparisons.forEach(comp => {
            const uiTierId = tierMap.get(comp.tierId);
            const vKey = comp.vendorKey;

            if (!newVendorCosts[itemId][vKey]) newVendorCosts[itemId][vKey] = {};
            if (!newShippingData[itemId][vKey]) newShippingData[itemId][vKey] = {};

            newVendorCosts[itemId][vKey][uiTierId] = comp.calculations?.productCost / comp.tierQuantity;

            newShippingData[itemId][vKey][uiTierId] = {
              ratePerKg: comp.shipping?.rate,
              method: comp.shipping?.method,
            };
          });

          const activeVendors = [...new Set(item.comparisons.map(c => c.vendorKey))];
          const firstComp = item.comparisons[0];
          const margin = parseInt(firstComp?.calculations?.profitMargin) || 50;

          return {
            id: itemId,
            name: item.itemName,
            packagingType: item.specs?.packagingType,
            printing: item.specs?.printing,
            finishes: item.specs?.finishes,
            lamination: item.specs?.lamination,
            stock: item.specs?.stock,
            dimLength: item.specs?.dimensions?.length,
            dimWidth: item.specs?.dimensions?.width,
            dimHeight: item.specs?.dimensions?.depth,
            dimUnit: item.specs?.dimensions?.unit,
            unitWeight: firstComp?.shipping?.weight / firstComp?.tierQuantity || 0,
            margin: margin,
            selectedVendors: activeVendors,
            tiers: uniqueTiers,
          };
        });

        setEstimateItems(newItems);
        setVendorCosts(newVendorCosts);
        setShippingData(newShippingData);

        // Set selected tiers for summary (defaulting to the first tier of each item)
        const selectedTiersMap = {};
        newItems.forEach(item => {
          selectedTiersMap[item.id] = [item.tiers[0]?.id];
        });
        setItemSelectedTiers(selectedTiersMap);

        const maxId = Math.max(...newItems.map(i => i.id), 0);
        setNextItemId(maxId + 1);
        return; // Exit if estimation found
      }
    }

    if (editId && product_enquires_data?.product_enquires?.length > 0) {
      const enquiry = product_enquires_data.product_enquires.find(e => e._id === editId || e.id === editId);
      if (enquiry) {
        console.log('Enquiry Found, Populating Form...', enquiry);
        const formValues = {
          clientName: enquiry.customer_id?.fullName,
          email: enquiry.customer_id?.email,
          phone: enquiry.customer_id?.phoneNumber,
          project_details: enquiry.description,
          companyName: '',
        };
        
        // Small delay to ensure Form components have registered their fields
        setTimeout(() => {
          form.setFieldsValue(formValues);
        }, 100);

        // Set estimate item details from enquiry
        setEstimateItems([
          {
            id: 1,
            name: enquiry.category?.title || 'Burger Box',
            packagingType: 'folding_carton',
            printing: 'cmyk_outside',
            finishes: 'spot_uv',
            lamination: 'matte',
            stock: '350gsm_e_flute',
            dimLength: enquiry.size?.length || 120,
            dimWidth: enquiry.size?.width || 120,
            dimHeight: enquiry.size?.depth || 80,
            dimUnit: enquiry.size?.unit || 'mm',
            unitWeight: 0.1,
            margin: 50,
            selectedVendors: ['pk'],
            tiers: [{ id: 101, quantity: enquiry.quantity || 500 }],
          },
        ]);

        // Reset search query or loading states if needed
      }
    }
  }, [editId, estimations_data, product_enquires_data, form]);

  // ── Item Handlers ──
  const addItem = () => {
    const newId = nextItemId;
    setEstimateItems(prev => [
      ...prev,
      {
        id: newId,
        name: `Item ${newId}`,
        packagingType: '',
        printing: '',
        finishes: '',
        lamination: '',
        stock: '',
        dimLength: '',
        dimWidth: '',
        dimHeight: '',
        dimUnit: 'mm',
        unitWeight: 0,
        margin: 50,
        selectedVendors: ['pk'],
        tiers: [{ id: Date.now(), quantity: 500 }],
      },
    ]);
    setNextItemId(p => p + 1);
  };

  const removeItem = id => {
    setEstimateItems(prev => prev.filter(item => item.id !== id));
  };

  const updateItem = (id, field, value) => {
    setEstimateItems(prev => prev.map(item => (item.id === id ? { ...item, [field]: value } : item)));
  };

  // ── Tier Handlers (Per Item) ──
  const addTier = itemId => {
    setEstimateItems(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, tiers: [...item.tiers, { id: Date.now() + Math.random(), quantity: '' }] }
          : item,
      ),
    );
  };

  const removeTier = (itemId, tierId) => {
    setEstimateItems(prev =>
      prev.map(item => (item.id === itemId ? { ...item, tiers: item.tiers.filter(t => t.id !== tierId) } : item)),
    );
  };

  const updateTierQty = (itemId, tierId, qty) => {
    setEstimateItems(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, tiers: item.tiers.map(t => (t.id === tierId ? { ...t, quantity: qty } : t)) }
          : item,
      ),
    );
  };

  const updateCost = (itemId, vendorKey, tierId, value) => {
    setVendorCosts(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [vendorKey]: {
          ...((prev[itemId] || {})[vendorKey] || {}),
          [tierId]: value,
        },
      },
    }));
  };

  const updateShipping = (itemId, vendorKey, tierId, field, value) => {
    setShippingData(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [vendorKey]: {
          ...((prev[itemId] || {})[vendorKey] || {}),
          [tierId]: {
            ...(((prev[itemId] || {})[vendorKey] || {})[tierId] || {}),
            [field]: value,
          },
        },
      },
    }));
  };

  // ── Computed data for Summary ──
  const totals = useMemo(() => {
    const res = { product: 0, shipping: 0, total: 0, grandTotal: 0 };
    estimateItems.forEach(item => {
      const activeTierId = itemSelectedTiers[item.id] || item.tiers[0]?.id;
      const activeTier = item.tiers.find(t => t.id === activeTierId) || item.tiers[0];

      const primaryVendor = item.selectedVendors?.[0] || 'pk';
      const vCost = Number(vendorCosts[item.id]?.[primaryVendor]?.[activeTier?.id] || 0);
      const qty = Number(activeTier?.quantity) || 0;
      const productTotal = vCost * qty;

      const sRate = Number(shippingData[item.id]?.[primaryVendor]?.[activeTier?.id]?.ratePerKg || 0);
      const sWeight = (Number(item.unitWeight) || 0) * qty;
      const shippingTotal = sWeight * sRate;

      const itemTotalCost = productTotal + shippingTotal;
      const itemFinalPrice = Math.round(itemTotalCost * (1 + (item.margin || 50) / 100) * 100) / 100;

      res.product += productTotal;
      res.shipping += shippingTotal;
      res.total += itemTotalCost;
      res.grandTotal += itemFinalPrice;
    });
    return res;
  }, [estimateItems, itemSelectedTiers, vendorCosts, shippingData]);

  // Comparison data for the table
  const comparisonData = useMemo(() => {
    const rows = [];
    estimateItems?.forEach(item => {
      item.tiers.forEach((tier, tIdx) => {
        const vendorData = {};
        VENDORS.forEach(v => {
          const qty = Number(tier.quantity) || 0;
          const cost = Number(vendorCosts[item.id]?.[v.key]?.[tier.id] || 0);
          const totalProduct = qty * cost;

          const sRate = Number(shippingData[item.id]?.[v.key]?.[tier.id]?.ratePerKg || 0);
          const sWeight = (Number(item.unitWeight) || 0) * qty;
          const totalShipping = sWeight * sRate;

          vendorData[v.key] = { totalProduct, totalShipping, totalCost: totalProduct + totalShipping };
        });
        rows.push({
          itemId: item.id,
          itemName: item.name,
          tierLabel: `Tier ${tIdx + 1}`,
          tierQty: tier.quantity,
          vendorData,
        });
      });
    });
    return rows;
  }, [estimateItems, vendorCosts, shippingData]);

  const generateJobId = useMemo(() => {
    const firstLetter = user?.email?.charAt(0)?.toUpperCase() + user?.email?.charAt(1)?.toUpperCase() || 'X';
    const now = new Date();
    const day = now.getDate();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();
    const dateStr = `${day}${month}${year}`;
    return `TPP${firstLetter}-${dateStr}-${totalToday + 1}`;
  }, [user, totalToday]);

  async function onSubmit(values) {
    const finalPayload = {
      jobId: generateJobId,
      client: {
        name: values.clientName,
        company: values.companyName,
        email: values.email,
        phone: values.phone,
        details: values.project_details,
        shippingAddresses: shippingAddresses.map((_, idx) => ({
          address: values[`shippingAddress_${idx}`],
          details: values[`shippingDetails_${idx}`],
        })),
      },
      vendorNotes: values.notes || '',
      items: estimateItems.map(item => {
        const selectedTierIds = itemSelectedTiers[item.id] || [item.tiers[0]?.id];

        const vendorTierComparisons = (item.selectedVendors || []).flatMap(vKey => {
          return selectedTierIds.map(tierId => {
            const tierIdx = item.tiers.findIndex(t => t.id === tierId);
            const activeTier = item.tiers[tierIdx];
            const vCost = Number(vendorCosts[item.id]?.[vKey]?.[activeTier?.id] || 0);
            const qty = Number(activeTier?.quantity) || 0;
            const prodTotal = vCost * qty;
            const sRate = Number(shippingData[item.id]?.[vKey]?.[activeTier?.id]?.ratePerKg || 0);
            const sMethod = shippingData[item.id]?.[vKey]?.[activeTier?.id]?.method || '';
            const sTotal = (Number(item.unitWeight) || 0) * qty * sRate;
            const itemTotal = prodTotal + sTotal;
            const finalPrice = Math.round(itemTotal * (1 + (item.margin || 50) / 100) * 100) / 100;

            return {
              vendor: VENDORS.find(v => v.key === vKey)?.label,
              vendorKey: vKey,
              tierId: tierIdx + 1,
              tierQuantity: qty,
              calculations: {
                productCost: prodTotal,
                shippingCost: sTotal,
                totalCost: itemTotal,
                profitMargin: `${item.margin}%`,
                finalPrice: finalPrice,
                profit: finalPrice - itemTotal,
              },
              unitPrices: {
                costPerUnit: qty > 0 ? itemTotal / qty : 0,
                sellingPricePerUnit: qty > 0 ? finalPrice / qty : 0,
              },
              shipping: {
                rate: sRate,
                method: sMethod,
                weight: (Number(item.unitWeight) || 0) * qty,
              },
            };
          });
        });

        return {
          id: item.id,
          itemName: item.name,
          specs: {
            packagingType: item.packagingType,
            printing: item.printing,
            finishes: item.finishes,
            lamination: item.lamination,
            stock: item.stock,
            dimensions: {
              length: item.dimLength,
              width: item.dimWidth,
              depth: item.dimHeight,
              unit: item.dimUnit,
            },
          },
          comparisons: vendorTierComparisons,
        };
      }),
    };

    try {
      setLoading(true);
      const res = editId
        ? await estimationService.updateEstimation(editId, finalPayload)
        : await estimationService.createEstimate(finalPayload);
      if (res?.success) {
        Toast({
          type: 'success',
          message: editId ? 'Estimation updated successfully' : 'Estimation created successfully',
        });
        navigate('/estimation');
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Something went wrong' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <SyledEstimation>
      <EstimateHeader
        generateJobId={generateJobId}
        isView={isView}
        loading={loading}
        onFormSubmit={() => form.submit()}
      />

      <Form form={form} onSubmit={onSubmit}>
        <ClientDetails
          isView={isView}
          shippingAddresses={shippingAddresses}
          addAddress={addAddress}
          removeAddress={removeAddress}
        />

        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Estimate Items</h3>
            {!isView && (
              <button type="button" className="btn-add-item" onClick={addItem}>
                <i className="material-icons-outlined">add</i> Add Item
              </button>
            )}
          </div>
          <div className="items-list">
            {estimateItems?.map((item, index) => (
              <EstimateItem
                key={item.id}
                item={item}
                index={index}
                isView={isView}
                updateItem={updateItem}
                removeItem={removeItem}
                addTier={addTier}
                removeTier={removeTier}
                updateTierQty={updateTierQty}
                vendorCosts={vendorCosts}
                updateCost={updateCost}
                shippingData={shippingData}
                updateShipping={updateShipping}
                itemSelectedTiers={itemSelectedTiers}
                setItemSelectedTiers={setItemSelectedTiers}
                options={{
                  packagingType: packagingTypeOpts,
                  print: printOptions,
                  finish: finishOptions,
                  lamination: laminationOptions,
                  stock: stockOptions,
                  unit: unitOpts,
                }}
                vendors={VENDORS}
                shippingMethods={SHIPPING_METHODS}
                fmt={fmt}
              />
            ))}
          </div>
        </div>

        <VendorComparison comparisonData={comparisonData} vendors={VENDORS} fmt={fmt} />

        <VendorNotes isView={isView} />
      </Form>
      {!isView && (
        <Button type="primary" loading={loading} className="btn-finalize-large" onClick={() => form.submit()}>
          Create Quote <i className="material-icons-outlined">arrow_forward</i>
        </Button>
      )}
    </SyledEstimation>
  );
};

export default CreateEstimate;
